import React, { FC, useMemo } from 'react'
import useTheme from '@material-ui/core/styles/useTheme'
import Paper from '@material-ui/core/Paper/Paper'
import { Box } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as ExpandIcon } from 'assets/svg/ExpandIcon.svg'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, YAxis } from 'recharts'
import IconButton from '@material-ui/core/IconButton'
import { IChartProps } from 'typescript/interfaces/dashboard'
import { convertDate, convertToPriceFormat, formatCurrency } from 'utils/formatters'

const PNLChart: FC<IChartProps> = ({ additionalRem = 0, onCollapse, stats, header }) => {
  const theme = useTheme()
  const fontSizeHtml = parseFloat(getComputedStyle(document.documentElement).fontSize)
  const data = stats?.data?.map(({ date, pnl }) => ({
    name: convertDate(new Date(date)),
    pnl,
  }))

  const maxLength = useMemo(() => {
    return data
      ? convertToPriceFormat(
          data.reduce((acc, val) => {
            return String(acc).length < String(val.pnl).length ? val.pnl : acc
          }, 0),
        ).toString().length *
          (fontSizeHtml / 2)
      : 0
  }, [data])

  return (
    <Paper elevation={0}>
      <Box height="100%" py="1rem" px="1rem" display="flex" flexDirection="column" justifyContent="space-between">
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body1">PNL</Typography>
            <IconButton onClick={onCollapse} size="small">
              <SvgIcon fontSize="small" viewBox="0 0 16 16" component={ExpandIcon} />
            </IconButton>
          </Box>
          {header && header.pnl !== undefined && (
            <Box>
              <Typography style={{ fontWeight: 600 }} variant="h4" noWrap color="textSecondary">
                {header.pnl >= 0 ? convertToPriceFormat(header.pnl) : `(${convertToPriceFormat(Math.abs(header.pnl))})`}
              </Typography>
            </Box>
          )}
        </Box>
        <Box mt={3}>
          <ResponsiveContainer width="100%" height={fontSizeHtml * (6.45 + additionalRem)}>
            <AreaChart
              data={data}
              margin={{
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <YAxis
                allowDecimals={false}
                tick={{
                  fill: theme.palette.text.primary,
                  strokeWidth: 1,
                }}
                minTickGap={0}
                tickCount={4}
                width={maxLength}
                tickFormatter={(item) => convertToPriceFormat(item)}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                tickMargin={0}
                padding={{
                  top: 0,
                  bottom: 0,
                }}
              />
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <Tooltip
                labelFormatter={(index) => data?.[index as number]?.name}
                formatter={(value) => {
                  if (Array.isArray(value)) {
                    return value.map((v) => formatCurrency(v)).join(', ')
                  }
                  return formatCurrency(value)
                }}
              />
              <Area type="monotone" dataKey="pnl" stroke={theme.palette.primary.main} fillOpacity={0.3} fill={theme.palette.primary.main} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  )
}

export default PNLChart
